import LoadingButton from '@mui/lab/LoadingButton';
import { styled } from '@mui/material/styles';
import React from 'react';

type ComponentSize = 'sm' | 'md' | 'lg';

interface ButtonProps {
  isLoading?: boolean;
  endIcon?: React.ReactNode;
  variant?: 'blue' | 'outlinedBlue' | 'red' | 'gray' | 'text' | 'blueFont' | 'outlinedRed';
  componentSize?: ComponentSize;
  children: React.ReactNode;
  [key: string]: any;
}

const StyleBtn = styled(LoadingButton, {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'componentSize',
})<{ variant?: any; componentSize?: ComponentSize }>(({
  theme,
  variant,
  componentSize = 'md',
}: any) => {
  const sizeTokens = theme.componentSizes.button[componentSize] || theme.componentSizes.button.md;
  return {
    borderRadius: `${theme.borderRadius.full}px`,
    minWidth: ['text', 'red']?.includes(variant) ? 'auto' : sizeTokens.minWidth,
    height: sizeTokens.height,
    padding: ['text', 'red']?.includes(variant) ? '0 10px' : sizeTokens.padding,
    fontWeight: 500,
    fontSize: sizeTokens.fontSize,
    textTransform: 'none',
    boxShadow: 'none',
    [theme.breakpoints.down('sm')]: {
      padding: '9px 5px',
      minWidth: ['text', 'red']?.includes(variant) ? 'auto' : '96px',
      fontSize: '0.875rem',
    },
    ...(variant === 'blue' && {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      border: `1px solid ${theme.palette.primary.main}`,
      '&:hover': {
        backgroundColor: theme.palette.primaryHover,
        border: `1px solid ${theme.palette.primary.main}`,
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.buttonDisabledBg,
        color: theme.palette.common.white,
      },
    }),
    ...(variant === 'outlinedBlue' && {
      backgroundColor: 'transparent',
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}`,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        border: `1px solid ${theme.palette.primary.main}`,
      },
      '&.Mui-disabled': {
        border: `1px solid ${theme.palette.borderDefault}`,
        color: theme.palette.buttonDisabledBg,
      },
    }),
    ...(variant === 'outlinedRed' && {
      backgroundColor: 'transparent',
      color: theme.palette.buttonRed,
      border: `1px solid ${theme.palette.buttonRed}`,
      '&:hover': {
        backgroundColor: theme.palette.buttonRed,
        color: theme.palette.common.white,
        border: `1px solid ${theme.palette.buttonRed}`,
      },
      '&.Mui-disabled': {
        border: `1px solid ${theme.palette.borderDefault}`,
        color: theme.palette.buttonDisabledBg,
      },
    }),
    ...(variant === 'red' && {
      backgroundColor: 'transparent',
      color: theme.palette.buttonRed,
      border: 'none',
      '&:hover': {
        color: theme.palette.buttonRed,
      },
    }),
    ...(variant === 'blueFont' && {
      backgroundColor: 'transparent',
      color: theme.palette.primary.main,
      border: 'none',
      '&:hover': {
        color: theme.palette.primary.main,
      },
    }),
    ...(variant === 'gray' && {
      backgroundColor: 'transparent',
      color: theme.palette.text.secondary,
      border: 'none',
    }),
    ...(variant === 'text' && {
      backgroundColor: theme.palette.common.white,
      border: 'none',
      '&:hover': {
        backgroundColor: theme.palette.common.white,
        border: `none`,
      },
      '&.Mui-disabled': {
        border: `1px solid ${theme.palette.borderDefault}`,
        color: theme.palette.buttonDisabledBg,
      },
    }),
  };
});

const Button: React.FC<ButtonProps> = ({
  isLoading = false,
  endIcon,
  variant = 'blue',
  componentSize = 'md',
  children,
  disabled: _disabled,
  ...props
}) => (
  <StyleBtn
    loading={isLoading}
    endIcon={endIcon}
    variant={variant as any}
    componentSize={componentSize}
    disabled={_disabled}
    {...props}
  >
    {children}
  </StyleBtn>
);

export default React.memo(Button);
